import { useState } from 'react';
import { Button } from '@lms/ui';
import { AlertCircle, Eye, EyeOff, LockKeyhole, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { copy } from '../copy/copy';
import { useAuthStore } from '../store/auth';
import { Bilingual } from './Bilingual';
import { useLanguage } from '../lib/LanguageContext';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const { t } = useLanguage();
  const { setAuth, token } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const closeAndReset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage('');
    onClose();
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirmPassword) {
        throw new Error(t(copy.profile.passwordMismatch));
      }
      if (newPassword.length < 8) {
        throw new Error(t(copy.profile.passwordMinLength));
      }
      await apiClient.auth.changePassword({ currentPassword, newPassword });
      return apiClient.auth.me();
    },
    onSuccess: updatedUser => {
      if (token) setAuth(token, updatedUser);
      closeAndReset();
    },
    onError: error => {
      setErrorMessage(error instanceof Error ? error.message : t(copy.profile.errorChangePassword));
    },
  });

  if (!isOpen) return null;

  const passwordFields = [
    {
      id: 'current-password',
      label: copy.profile.currentPasswordLabel,
      value: currentPassword,
      onChange: setCurrentPassword,
      visible: showCurrent,
      setVisible: setShowCurrent,
      autoComplete: 'current-password',
    },
    {
      id: 'new-password',
      label: copy.profile.newPasswordLabel,
      value: newPassword,
      onChange: setNewPassword,
      visible: showNew,
      setVisible: setShowNew,
      autoComplete: 'new-password',
    },
    {
      id: 'confirm-new-password',
      label: copy.profile.confirmNewPasswordLabel,
      value: confirmPassword,
      onChange: setConfirmPassword,
      visible: showConfirm,
      setVisible: setShowConfirm,
      autoComplete: 'new-password',
    },
  ];

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/45 p-4 backdrop-blur-sm"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        className="w-full max-w-md overflow-hidden rounded-[1.5rem] bg-surface-container-lowest shadow-level3"
      >
        <header className="flex items-center justify-between gap-4 bg-surface-container-low p-5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-secondary-container text-on-secondary-container">
              <LockKeyhole size={19} aria-hidden="true" />
            </div>
            <div>
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                <Bilingual {...copy.profile.accountSettings} />
              </p>
              <h3
                id="change-password-title"
                className="m-0 mt-1 text-lg font-headline font-bold text-on-surface"
              >
                <Bilingual {...copy.profile.changePassword} />
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={closeAndReset}
            aria-label={t(copy.common.close)}
            className="flex size-9 items-center justify-center rounded-xl text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <form
          onSubmit={event => {
            event.preventDefault();
            setErrorMessage('');
            updateMutation.mutate();
          }}
          className="space-y-5 p-5"
        >
          {errorMessage && (
            <div
              className="flex items-start gap-3 rounded-xl bg-error-container p-3 text-sm text-on-error-container"
              role="alert"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          {passwordFields.map(field => (
            <div key={field.id}>
              <label
                htmlFor={field.id}
                className="mb-2 block text-sm font-label font-bold text-on-surface"
              >
                <Bilingual {...field.label} />
              </label>
              <div className="relative">
                <input
                  id={field.id}
                  type={field.visible ? 'text' : 'password'}
                  autoComplete={field.autoComplete}
                  required
                  minLength={field.id === 'current-password' ? undefined : 8}
                  value={field.value}
                  onChange={event => field.onChange(event.target.value)}
                  className="w-full rounded-xl bg-surface-container-high px-4 py-3 pr-12 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
                />
                <button
                  type="button"
                  onClick={() => field.setVisible(!field.visible)}
                  aria-label={t(
                    field.visible ? copy.profile.hidePassword : copy.profile.showPassword
                  )}
                  className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-lowest hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                >
                  {field.visible ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAndReset}
              disabled={updateMutation.isPending}
            >
              <Bilingual {...copy.common.cancel} />
            </Button>
            <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                t(copy.profile.saving)
              ) : (
                <Bilingual {...copy.common.save} />
              )}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
